import React from 'react';
import { css } from '@emotion/react';
import { color } from '@uniquegood/realworld-studio-design';
import { onMobile } from '@/styles/responsive';

interface CheckListProps {
  // 각 섹션의 체크 상태
  genreChecked: boolean;
  backgroundChecked: boolean;
  atmosphereChecked: boolean;
  storyGoalChecked: boolean;
  mainEventChecked: boolean;
  endingChecked: boolean;
  purposeChecked: boolean;
  // 전체 폼 유효성 상태
  isAllRequiredChecked: boolean;
  onClick: () => void;
}

interface CheckListItemProps {
  label: string;
  isChecked: boolean;
  isOptional?: boolean;
}

function CheckListItem({ label, isChecked, isOptional = false }: CheckListItemProps) {
  return (
    <div css={itemStyle}>
      <span css={textStyle(isChecked)}>
        {label}
        {isOptional && ' (선택)'}
      </span>
      <div css={checkboxStyle(isChecked)}>
        {isChecked && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            css={checkIconStyle}
          >
            <path
              d="M10 3L4.5 8.5L2 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </div>
  );
}

function CheckList({
  genreChecked,
  backgroundChecked,
  atmosphereChecked,
  storyGoalChecked,
  mainEventChecked,
  endingChecked,
  purposeChecked,
  isAllRequiredChecked,
  onClick
}: CheckListProps) {
  return (
    <div css={containerStyle}>
      <div css={contentStyle}>
        <CheckListItem label="장르" isChecked={genreChecked} />
        <CheckListItem label="스토리 배경" isChecked={backgroundChecked} />
        <CheckListItem label="분위기" isChecked={atmosphereChecked} />
        <CheckListItem label="목표" isChecked={storyGoalChecked} />
        <CheckListItem label="중심 사건" isChecked={mainEventChecked} />
        <CheckListItem label="결말" isChecked={endingChecked} />
        <CheckListItem label="제작 목적" isChecked={purposeChecked} isOptional />
      </div>

      <div css={buttonContainerStyle}>
        <button
          type="button"
          css={buttonStyle(isAllRequiredChecked)}
          disabled={!isAllRequiredChecked}
          onClick={onClick}
        >
          스토리 만들기
        </button>
      </div>
    </div>
  );
}

const containerStyle = css`
  width: 160px;
  background-color: ${color.surface_default_default};
  border-radius: 16px;
  box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.07);
  overflow: hidden;

  /* 모바일에서만 숨김 */
  ${onMobile} {
    display: none;
  }
`;

const contentStyle = css`
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const itemStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  border-radius: 4px;
  background-color: ${color.surface_default_default};
`;

const textStyle = (isChecked: boolean) => css`
  font-family: 'Pretendard', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: ${isChecked ? '#7800ff' : '#a9a9a9'};
  margin: 0;
`;

const checkboxStyle = (isChecked: boolean) => css`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid ${isChecked ? '#7800ff' : '#c3c3c3'};
  background-color: ${isChecked ? '#7800ff' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const checkIconStyle = css`
  width: 12px;
  height: 12px;
`;

const buttonContainerStyle = css`
  padding: 8px;
`;

const buttonStyle = (isEnabled: boolean) => css`
  width: 100%;
  padding: 8px 16px;
  border-radius: 12px;
  border: none;
  background-color: ${isEnabled ? '#7800ff' : '#ededed'};
  color: ${isEnabled ? '#ffffff' : '#a9a9a9'};
  font-family: 'Pretendard', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  cursor: ${isEnabled ? 'pointer' : 'not-allowed'};

  &:hover {
    background-color: ${isEnabled ? '#6700e6' : '#ededed'};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export default CheckList;